import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../../api";
import drIsInLogo from "../../assets/dr-is-in.png";
import "./StudentTipsPage.css";

type TipItem = {
  id?: number | string;
  title?: string;
  body?: string;
  image_url?: string | null;
  category?: string;
};

function getRole(): string {
  return (localStorage.getItem("role") || sessionStorage.getItem("role") || "").toLowerCase();
}

function clean(v: any) {
  return String(v ?? "").trim();
}

function resolveUrl(raw?: string | null) {
  const v = clean(raw);
  if (!v) return "";
  if (v.startsWith("http://") || v.startsWith("https://")) return v;
  const base = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");
  return v.startsWith("/") ? `${base}${v}` : `${base}/${v}`;
}

function getPortalReturnPath(location: any): string {
  const fromState = location?.state?.from;
  if (typeof fromState === "string" && fromState.trim()) return fromState.trim();

  const role = getRole();
  const ptReturn = sessionStorage.getItem("pt_portal_return");
  const studentReturn = sessionStorage.getItem("student_portal_return");

  if ((role === "pt" || role === "parent" || role === "teacher") && ptReturn?.trim()) {
    return ptReturn.trim();
  }

  if (studentReturn?.trim()) {
    return studentReturn.trim();
  }

  if (role === "pt" || role === "parent" || role === "teacher") {
    return "/parent-teacher-portal";
  }

  return "/student-portal";
}

function prettyCategory(cat: string) {
  const c = clean(cat).toLowerCase();
  if (c === "physical") return "Physical Wellness";
  if (c === "mental") return "Mental Health & Stress";
  if (c === "everyday") return "Everyday Healthy Habits";
  return cat || "Tips";
}

function categorySubtitle(cat: string, role: string) {
  const isAdult = role === "pt" || role === "parent" || role === "teacher";
  const who = isAdult ? "parents, teachers, and caregivers" : "students";

  const c = clean(cat).toLowerCase();
  if (c === "physical") return `Simple body-health supports for ${who}.`;
  if (c === "mental") return `Calm, focus, anxiety, and reset tools for ${who}.`;
  if (c === "everyday") return `Small everyday habits that build confidence and consistency.`;
  return isAdult ? "Helpful wellness tips for parents and teachers." : "Helpful wellness tips for students.";
}

function normalizeTips(data: any): TipItem[] {
  const arr =
    (Array.isArray(data) && data) ||
    (Array.isArray(data?.tips) && data.tips) ||
    (Array.isArray(data?.items) && data.items) ||
    [];

  return arr.map((x: any) => ({
    id: x?.id,
    title: clean(x?.title),
    body: clean(x?.body || x?.content || x?.tip_text || x?.description),
    image_url: x?.image_url || x?.image || x?.photo_url || null,
    category: clean(x?.category),
  }));
}

function splitTipBody(body: string): string[] {
  const text = clean(body);
  if (!text) return [];

  // Preserve intentional line breaks when the backend sends grouped tips.
  const lineParts = text
    .split(/\r?\n+/)
    .map((x) => clean(x))
    .filter(Boolean);

  if (lineParts.length > 1) return lineParts;

  return [text];
}

export default function StudentTipsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { category = "" } = useParams();

  const [tips, setTips] = useState<TipItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const portalReturn = useMemo(() => getPortalReturnPath(location), [location]);
  const role = getRole();

  useEffect(() => {
    sessionStorage.setItem("student_portal_return", "/student-portal");

    const load = async () => {
      setLoading(true);
      setErr("");

      try {
        const res = await api.get(`/tips/public?category=${encodeURIComponent(category)}`);
        setTips(normalizeTips(res.data));
      } catch (e: any) {
        const detail = e?.response?.data?.detail ?? e?.message ?? "Failed to load tips.";
        setErr(typeof detail === "string" ? detail : JSON.stringify(detail));
        setTips([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [category]);

  const handleBack = () => {
    navigate(portalReturn || "/student-portal", { replace: true });
  };

  return (
    <div className="tipsPage">
      <div className="tipsPageShell">
        <div className="tipsPageTop">
          <button className="tipsPageBack" type="button" onClick={handleBack}>
            ← Back to Chat
          </button>

          <div className="tipsPageBrand">
            <img src={drIsInLogo} className="tipsPageLogo" alt="Dr Is In App" />
            <div>
              <div className="tipsPageTitle">{prettyCategory(category)}</div>
              <div className="tipsPageSub">{categorySubtitle(category, role)}</div>
            </div>
          </div>
        </div>

        {loading ? <div className="tipsPageNotice">Loading tips...</div> : null}
        {err ? <div className="tipsPageNotice error">{err}</div> : null}

        <div className="tipsPageGrid">
          {!loading && !err && tips.length === 0 ? (
            <div className="tipsPageNotice">No tips found.</div>
          ) : (
            tips.map((tip, idx) => {
              const bodyLines = splitTipBody(tip.body || "");
              return (
                <article key={String(tip.id ?? idx)} className="tipsPageCard">
                  {tip.image_url ? (
                    <img
                      src={resolveUrl(tip.image_url)}
                      alt={tip.title || "Tip"}
                      className="tipsPageImage"
                    />
                  ) : null}

                  <div className="tipsPageCardContent">
                    <div className="tipsPageCardKicker">Wellness Tip</div>
                    <h2 className="tipsPageCardTitle">{tip.title || "Tip"}</h2>

                    <div className="tipsPageCardBody">
                      {bodyLines.map((line, lineIdx) => (
                        <p key={`${tip.id ?? idx}-${lineIdx}`}>{line}</p>
                      ))}
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
